import React, { useState } from 'react';

const FontSettings = ({ 
  theme, 
  onThemeUpdate,
  campaignId,
  api,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError
}) => {
  const [uploadingHeaderFont, setUploadingHeaderFont] = useState(false);
  const [uploadingParagraphFont, setUploadingParagraphFont] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleThemeUpdate = (field, value) => {
    onThemeUpdate(field, value);
  };

  const handleHeaderFontUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['ttf', 'otf'].includes(ext)) {
      if (setErrorMessage && setShowError) {
        setErrorMessage('Please upload a TTF or OTF font file');
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } else {
        alert('Please upload a TTF or OTF font file');
      }
      return;
    }

    setUploadingHeaderFont(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'header');

      const response = await api.post(`/admin/campaigns/${campaignId}/fonts/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Get font name (use existing or derive from filename)
      const fontName = theme?.font_settings?.header_font_name || file.name.replace(/\.[^/.]+$/, '');
      const fontFileUrl = response.data.url;
      
      // Update theme state (similar to logo upload pattern)
      handleThemeUpdate('header_font_file_url', fontFileUrl);
      if (!theme?.font_settings?.header_font_name) {
        handleThemeUpdate('header_font_name', fontName);
      }
      
      // Show success message
      if (setSuccessMessage && setShowSuccess) {
        setSuccessMessage('Header font uploaded successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        alert('Header font uploaded successfully!');
      }
    } catch (error) {
      console.error('Failed to upload header font:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload font file. Please try again.';
      if (setErrorMessage && setShowError) {
        setErrorMessage(errorMessage);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploadingHeaderFont(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleParagraphFontUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['ttf', 'otf'].includes(ext)) {
      if (setErrorMessage && setShowError) {
        setErrorMessage('Please upload a TTF or OTF font file');
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } else {
        alert('Please upload a TTF or OTF font file');
      }
      return;
    }

    setUploadingParagraphFont(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'paragraph');

      const response = await api.post(`/admin/campaigns/${campaignId}/fonts/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Get font name (use existing or derive from filename)
      const fontName = theme?.font_settings?.paragraph_font_name || file.name.replace(/\.[^/.]+$/, '');
      const fontFileUrl = response.data.url;
      
      // Update theme state (similar to logo upload pattern)
      handleThemeUpdate('paragraph_font_file_url', fontFileUrl);
      if (!theme?.font_settings?.paragraph_font_name) {
        handleThemeUpdate('paragraph_font_name', fontName);
      }
      
      // Show success message
      if (setSuccessMessage && setShowSuccess) {
        setSuccessMessage('Paragraph font uploaded successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        alert('Paragraph font uploaded successfully!');
      }
    } catch (error) {
      console.error('Failed to upload paragraph font:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload font file. Please try again.';
      if (setErrorMessage && setShowError) {
        setErrorMessage(errorMessage);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploadingParagraphFont(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleRemoveFont = (type) => {
    if (type === 'header') {
      handleThemeUpdate('header_font_file_url', '');
      handleThemeUpdate('header_font_name', '');
    } else {
      handleThemeUpdate('paragraph_font_file_url', '');
      handleThemeUpdate('paragraph_font_name', '');
    }
  };

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple simultaneous saves
    
    setIsSaving(true);
    try {
      // Prepare theme data with font settings
      // Convert empty strings to null to avoid SQL errors
      const themeData = {
        header_font_name: (theme?.font_settings?.header_font_name && theme.font_settings.header_font_name.trim()) ? theme.font_settings.header_font_name : null,
        header_font_file_url: (theme?.font_settings?.header_font_file_url && theme.font_settings.header_font_file_url.trim()) ? theme.font_settings.header_font_file_url : null,
        paragraph_font_name: (theme?.font_settings?.paragraph_font_name && theme.font_settings.paragraph_font_name.trim()) ? theme.font_settings.paragraph_font_name : null,
        paragraph_font_file_url: (theme?.font_settings?.paragraph_font_file_url && theme.font_settings.paragraph_font_file_url.trim()) ? theme.font_settings.paragraph_font_file_url : null
      };
      
      await api.put(`/admin/campaigns/${campaignId}/theme`, themeData);
      
      if (setSuccessMessage && setShowSuccess) {
        setSuccessMessage('Font settings saved successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        alert('Font settings saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save font settings:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save font settings. Please try again.';
      if (setErrorMessage && setShowError) {
        setErrorMessage(errorMessage);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Font Settings</h3>
        <p className="text-sm text-gray-500 mb-6">Upload custom fonts for your campaign. Fonts will be applied to headings and body text throughout your campaign pages.</p>
        
        <div className="space-y-6">
          {/* Header Font */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Header Font</label>
            <p className="text-xs text-gray-500 mb-4">Used for headings (h1, h2, h3, etc.)</p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Name</label>
                <input
                  type="text"
                  value={theme?.font_settings?.header_font_name || ''}
                  onChange={(e) => handleThemeUpdate('header_font_name', e.target.value)}
                  placeholder="Enter font name (e.g., Custom Header Font)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font File</label>
                {theme?.font_settings?.header_font_file_url ? (
                  <div className="flex items-center space-x-4 mb-2">
                    <span className="text-sm text-gray-600">
                      {theme.font_settings.header_font_file_url.split('/').pop()}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFont('header')}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
                <label className="cursor-pointer inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  <span>{uploadingHeaderFont ? 'Uploading...' : 'Upload Font (TTF/OTF)'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept=".ttf,.otf"
                    onChange={handleHeaderFontUpload}
                    disabled={uploadingHeaderFont}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Paragraph Font */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Paragraph Font</label>
            <p className="text-xs text-gray-500 mb-4">Used for paragraphs, labels, and body text</p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Name</label>
                <input
                  type="text"
                  value={theme?.font_settings?.paragraph_font_name || ''}
                  onChange={(e) => handleThemeUpdate('paragraph_font_name', e.target.value)}
                  placeholder="Enter font name (e.g., Custom Paragraph Font)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font File</label>
                {theme?.font_settings?.paragraph_font_file_url ? (
                  <div className="flex items-center space-x-4 mb-2">
                    <span className="text-sm text-gray-600">
                      {theme.font_settings.paragraph_font_file_url.split('/').pop()}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFont('paragraph')}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
                <label className="cursor-pointer inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  <span>{uploadingParagraphFont ? 'Uploading...' : 'Upload Font (TTF/OTF)'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept=".ttf,.otf"
                    onChange={handleParagraphFontUpload}
                    disabled={uploadingParagraphFont}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="border-t pt-6">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={`px-6 py-3 rounded-xl font-medium flex items-center space-x-2 ${
            isSaving 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-teal-700 hover:bg-teal-600 cursor-pointer'
          } text-white`}
        >
          {isSaving && (
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span>{isSaving ? 'Saving...' : 'Save Font Settings'}</span>
        </button>
      </div>
    </div>
  );
};

export default FontSettings;

